import {
  MAXIMUM_DAYS_OF_RENT,
  MINIMUM_DAYS_OF_RENT,
} from 'ks-common/dist/order-quote/constants/order-quote.constants';

export { MAXIMUM_DAYS_OF_RENT, MINIMUM_DAYS_OF_RENT };

export type IsoDate = string;

export const RENTAL_COPY = {
  maxPeriod: `Maximum rental period is ${MAXIMUM_DAYS_OF_RENT} days.`,
  deliveryFirst: 'Select a delivery date first.',
  returnCleared: `Return date cleared. It must be within ${MAXIMUM_DAYS_OF_RENT} days of delivery, so please pick a new one.`,
  tooLong: `You exceeded the rental limit of ${MAXIMUM_DAYS_OF_RENT} days`,
  beforeDelivery: 'The Return date must be after the Delivery date',
  tooShort: `The dates difference must be at least ${MINIMUM_DAYS_OF_RENT} day`,
} as const;

const DAY_MS = 24 * 60 * 60 * 1000;

const toUtc = (day: IsoDate): number => {
  const [year, month, date] = day.split('-').map(Number);
  return Date.UTC(year, month - 1, date);
};

const pad = (n: number) => String(n).padStart(2, '0');

export const todayIso = (now: Date = new Date()): IsoDate =>
  `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

export const addDays = (day: IsoDate, n: number): IsoDate =>
  new Date(toUtc(day) + n * DAY_MS).toISOString().slice(0, 10);

export const rentalDays = (delivery: IsoDate, ret: IsoDate): number =>
  Math.round((toUtc(ret) - toUtc(delivery)) / DAY_MS);

export interface DayBounds {
  min: IsoDate;
  max?: IsoDate;
}

export const deliveryBounds = (today: IsoDate): DayBounds => ({
  min: addDays(today, 1),
});

export const returnBounds = (delivery: IsoDate): Required<DayBounds> => ({
  min: addDays(delivery, MINIMUM_DAYS_OF_RENT),
  max: addDays(delivery, MAXIMUM_DAYS_OF_RENT),
});

export const isWithin = (day: IsoDate, { min, max }: DayBounds): boolean =>
  day >= min && (max === undefined || day <= max);

/**
 * Blocked_Dates is a raw string owned by the legacy DB, so a day is blocked when
 * its ISO form appears anywhere in it.
 */
export const isBlocked = (
  blockedDates: string | undefined,
  day: IsoDate,
): boolean => !!blockedDates?.includes(day);

export interface RentalDates {
  deliveryDate: IsoDate | null;
  returnDate: IsoDate | null;
}

export const changeDelivery = (
  { returnDate }: RentalDates,
  next: IsoDate | null,
): { patch: RentalDates; returnCleared: boolean } => {
  const returnCleared =
    next !== null &&
    returnDate !== null &&
    !isWithin(returnDate, returnBounds(next));

  return {
    patch: {
      deliveryDate: next,
      returnDate: returnCleared ? null : returnDate,
    },
    returnCleared,
  };
};

export const rentalRangeError = ({
  deliveryDate,
  returnDate,
}: RentalDates): string => {
  if (!deliveryDate || !returnDate) return '';

  const days = rentalDays(deliveryDate, returnDate);
  if (days > MAXIMUM_DAYS_OF_RENT) return RENTAL_COPY.tooLong;
  if (days < 0) return RENTAL_COPY.beforeDelivery;
  if (days < MINIMUM_DAYS_OF_RENT) return RENTAL_COPY.tooShort;
  return '';
};
